/**
 * useChangeVideo Hook
 * Replaces the profile video: soft-deletes the existing native videos, then
 * uploads a new one through the native pipeline (single-video UX).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { probeVideoDuration, uploadResumable } from "@/features/rituals/utils/gcsResumableUpload"
import {
  type ConfirmUploadResponse,
  confirmProfileVideoUpload,
  deleteProfileVideo,
  listProfileVideos,
  requestProfileVideoUploadUrl,
} from "../services/profileVideo.service"

async function changeProfileVideo(userId: string, file: File): Promise<ConfirmUploadResponse> {
  // Replace semantics: drop the existing native videos first (best-effort, but
  // surfaced -- a silently-failed delete could leave the old video alongside
  // the new one; the orphan sweep won't catch a row that never got soft-deleted).
  const existing = await listProfileVideos(userId)
  const results = await Promise.allSettled(existing.map(v => deleteProfileVideo(userId, v.id)))
  const failed = results.filter(r => r.status === "rejected").length
  if (failed > 0) {
    console.warn(`changeProfileVideo: ${failed} old profile video(s) failed to delete`)
  }

  let durationSeconds = 0
  try {
    const probed = await probeVideoDuration(file)
    if (probed > 0) durationSeconds = Math.round(probed)
  } catch {
    durationSeconds = 0
  }

  const upload = await requestProfileVideoUploadUrl(userId, {
    filename: file.name,
    contentType: file.type || "video/mp4",
    sizeBytes: file.size,
    durationSeconds,
  })
  await uploadResumable({ signedUrl: upload.signedUrl, file, contentType: upload.contentType })
  return confirmProfileVideoUpload(userId, { gcsPath: upload.gcsPath, title: file.name })
}

export function useChangeVideo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, file }: { userId: string; file: File }) =>
      changeProfileVideo(userId, file),
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["video", variables.userId] })
    },
  })
}
